import { createFileRoute } from '@tanstack/react-router'
import HomePage from '../components/HomePage'
import {
  HOME_ALL_PRODUCTS_PRIORITY_SLUGS,
  HOME_BESTSELLER_PRODUCT_SLUGS,
  HOME_FAQ_ITEMS,
  HOME_NEW_PRODUCT_SLUGS,
  HOME_POPULAR_PRODUCT_SLUGS,
  mergeHomeAllProducts,
} from '../data/home'
import {
  getStorefrontProduct,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontProduct } from '../lib/dropify'
import { listDashboardNewsPosts } from '../lib/news'
import { listBlogPosts } from '../lib/blog'
import { DEFAULT_OG_IMAGE, SITE_URL, absoluteUrl, buildFaqSchema } from '../lib/seo'

const HERO_PRELOAD_TRANSFORM = { width: 1200, height: 600, fitIn: true, format: 'webp', quality: 64 } as const
const HOME_PRODUCT_REQUEST_CONCURRENCY = 5
const HOME_CONFIG_WAIT_MS = 1_500

const EMPTY_PRODUCTS_RESPONSE = {
  items: [] as StorefrontProduct[],
  total: 0,
  page: 1,
  per_page: 12,
  total_pages: 0,
}

async function loadHomeProductSections() {
  const productsResponse = await listStorefrontProducts({ per_page: 12 }).catch(() => EMPTY_PRODUCTS_RESPONSE)

  const productsBySlug = productsResponse.total > 0
    ? await loadHomeProductsBySlug([
        ...HOME_ALL_PRODUCTS_PRIORITY_SLUGS,
        ...HOME_POPULAR_PRODUCT_SLUGS,
        ...HOME_NEW_PRODUCT_SLUGS,
        ...HOME_BESTSELLER_PRODUCT_SLUGS,
      ])
    : new Map<string, StorefrontProduct>()

  return {
    products: mergeHomeAllProducts(
      selectHomeProducts(HOME_ALL_PRODUCTS_PRIORITY_SLUGS, productsBySlug),
      productsResponse.items,
    ),
    popularProducts: selectHomeProducts(HOME_POPULAR_PRODUCT_SLUGS, productsBySlug),
    newProducts: selectHomeProducts(HOME_NEW_PRODUCT_SLUGS, productsBySlug),
    bestsellerProducts: selectHomeProducts(HOME_BESTSELLER_PRODUCT_SLUGS, productsBySlug),
  }
}

async function loadHomeConfig() {
  let timeout: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<null>((resolve) => {
    timeout = setTimeout(() => resolve(null), HOME_CONFIG_WAIT_MS)
  })

  try {
    return await Promise.race([getStorefrontConfig().catch(() => null), deadline])
  } finally {
    clearTimeout(timeout)
  }
}

async function loadHomeProductsBySlug(slugs: readonly string[]) {
  const pendingSlugs = [...new Set(slugs)]
  const productsBySlug = new Map<string, StorefrontProduct>()
  let cursor = 0

  async function worker() {
    while (cursor < pendingSlugs.length) {
      const slug = pendingSlugs[cursor]
      cursor += 1
      const product = await getStorefrontProduct(slug).catch(() => null)
      if (product) productsBySlug.set(slug, product)
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(HOME_PRODUCT_REQUEST_CONCURRENCY, pendingSlugs.length) },
      () => worker(),
    ),
  )

  return productsBySlug
}

function selectHomeProducts(
  slugs: readonly string[],
  productsBySlug: Map<string, StorefrontProduct>,
) {
  return slugs
    .map((slug) => productsBySlug.get(slug))
    .filter((product): product is StorefrontProduct => product !== undefined)
}

export const Route = createFileRoute('/')({
  loader: async () => {
    const configPromise = loadHomeConfig()
    const categoriesPromise = listStorefrontCategories().catch(() => [])
    const productDataPromise = loadHomeProductSections()
    const newsPostsPromise = listDashboardNewsPosts().catch(() => [])
    const blogPostsPromise = listBlogPosts({ data: { limit: 6 } }).catch(() => [])
    const [config, categories] = await Promise.all([configPromise, categoriesPromise])

    return {
      config,
      categories,
      productDataPromise,
      newsPostsPromise,
      blogPostsPromise,
    }
  },
  head: ({ loaderData }) => {
    const title = 'Dropify | Grosir Distributor Tangan Pertama'
    const description = 'Dropify adalah distributor grosir di Surabaya untuk pakaian dalam, kaos kaki, handuk, sarung tangan, masker, dan perlengkapan lain. Pemesanan dilakukan melalui WhatsApp.'
    const firstBannerImage = loaderData?.config?.banner_image_urls?.find(Boolean)
    const heroPreloadImage = firstBannerImage ?? 'https://placehold.co/1400x520/1e3a5f/3b82f6?text='

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: SITE_URL },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [
        {
          rel: 'preload',
          as: 'image',
          href: toExternalImage(heroPreloadImage, HERO_PRELOAD_TRANSFORM),
          media: '(min-width: 768px)',
          fetchPriority: 'high',
        },
        { rel: 'canonical', href: absoluteUrl('/') },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(HOME_FAQ_ITEMS)),
        },
      ],
    }
  },
  component: HomeRoute,
})

function HomeRoute() {
  const data = Route.useLoaderData()
  return <HomePage {...data} />
}
